/** Record one spoken turn, and know when it has finished.
 *
 * This replaces the browser's own speech recogniser as the thing that
 * LISTENS. The recogniser was the cause of both halves of the client's
 * complaint: it decided a sentence was over after about a second of
 * silence — so a learner who paused to think was cut off mid-thought
 * ("stopping in between") — and its German was whatever the device had,
 * which on a phone is not much ("not listening clearly"). The audio now
 * goes to the server, where one good model hears everyone the same.
 *
 * That leaves this module with exactly one hard job: deciding when a turn
 * is over. It does it by SILENCE, measured on the microphone level, and it
 * waits far longer than the recogniser did — long enough for a language
 * learner to find a word. Pausing is thinking, not finishing. And because
 * no timer can know the difference for certain, the caller also gets
 * `finish()`, which the page puts on a button.
 *
 * Browser APIs are injected through small factories so the whole thing is
 * testable in jsdom, which has neither a microphone nor a MediaRecorder.
 */

export interface RecorderOptions {
  /** One finished turn: the audio, and how long it ran. */
  onClip: (clip: Blob, seconds: number, mime: string) => void;
  /** Microphone level 0..1, a few times a second, while listening — so the
   *  learner can SEE they are being heard. */
  onLevel?: (level: number) => void;
  onError?: (error: unknown) => void;
  /** Silence after speech that ends the turn - the STARTING window. */
  silenceMs?: number;
  /** The most the window may stretch to as the learner's own pauses are
   *  observed. See silenceWindowFor() for why it stretches at all. */
  maxSilenceMs?: number;
  /** A turn can never run longer than this. */
  maxMs?: number;
  /** Less speech than this is a cough, not a turn — re-arm instead. */
  minSpeechMs?: number;
}

export interface Recorder {
  /** Open the microphone and start listening for a turn. */
  start(): Promise<void>;
  /** The learner says they are done: deliver what was recorded. */
  finish(): void;
  /** Stop listening and discard anything half-recorded. */
  stop(): void;
}

/** How often the level is sampled. Also the clock for the silence timer. */
const TICK_MS = 50;
/** Fire `onLevel` every other tick: ten updates a second is plenty for a
 *  meter, and twenty is a React re-render storm. */
const LEVEL_EVERY = 2;
/** Absolute floor under which nothing counts as speech, whatever the room. */
const MIN_SPEECH_LEVEL = 0.02;
/** Speech must rise this far above the measured noise floor. */
const ABOVE_FLOOR = 3;
/** The noise floor can never be learned higher than this, so a noisy room
 *  raises the bar a little and a loud learner cannot raise it at all. */
const MAX_FLOOR = 0.08;
/** How quickly the floor follows the room: a slow average of QUIET frames.
 *  Learning it from every frame was a real bug - a learner who started
 *  talking the moment the mic opened taught the detector that their voice
 *  was the silence, and nothing they said after that counted as speech. */
const FLOOR_EASE = 0.05;

const DEFAULTS = { silenceMs: 1800, maxSilenceMs: 3000, maxMs: 60_000, minSpeechMs: 400 };

/** A pause mid-sentence that was shorter than this is just speech rhythm,
 *  not a pause worth learning from. */
const RESUMED_PAUSE_MIN_MS = 300;
/** How far the window stretches past the longest pause the learner has
 *  actually come back from: half again, plus a breath. */
const RESUMED_PAUSE_FACTOR = 1.5;
const RESUMED_PAUSE_SLACK_MS = 500;

/** The silence window for a learner at this level: where it starts, and
 *  how far it may stretch.
 *
 * A single number cannot be right for both an eight-year-old at A1 and an
 * adult at C1. Beginners speak slowly and stop for two or three seconds to
 * find a word; the first version of this used 1.8 seconds for everyone,
 * which would have cut exactly those learners off mid-thought - the
 * client's complaint in a softer form. So the window STARTS wider for
 * lower levels, and the recorder then stretches it further for the
 * individual (see sample()), up to this ceiling.
 */
export function silenceWindowFor(level?: string | null): { silenceMs: number; maxSilenceMs: number } {
  const code = (level ?? "").trim().toUpperCase();
  switch (code) {
    case "A1": return { silenceMs: 3500, maxSilenceMs: 5000 };
    case "A2": return { silenceMs: 3200, maxSilenceMs: 4800 };
    case "B1": return { silenceMs: 2600, maxSilenceMs: 4000 };
    case "B2": return { silenceMs: 2200, maxSilenceMs: 3500 };
    case "C1":
    case "C2": return { silenceMs: 1800, maxSilenceMs: 3000 };
    default: return { silenceMs: 2600, maxSilenceMs: 4000 };
  }
}

/** The container the browser can actually produce, and the filename the
 *  server should see for it. Order matters: Chrome, Firefox and Android
 *  take the first; Safari only the second. */
const MIME_CHOICES: { mime: string; filename: string }[] = [
  { mime: "audio/webm;codecs=opus", filename: "turn.webm" },
  { mime: "audio/webm", filename: "turn.webm" },
  { mime: "audio/mp4", filename: "turn.mp4" },
  { mime: "audio/ogg;codecs=opus", filename: "turn.ogg" },
];

type RecorderCtor = typeof MediaRecorder;

function recorderCtor(): RecorderCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { MediaRecorder?: RecorderCtor };
  return w.MediaRecorder ?? null;
}

export function recorderAvailable(): boolean {
  return (
    recorderCtor() !== null &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window !== "undefined" &&
    !!(window as unknown as { AudioContext?: unknown }).AudioContext
  );
}

export function pickMime(isSupported: (m: string) => boolean): { mime: string; filename: string } {
  return MIME_CHOICES.find((c) => isSupported(c.mime)) ?? { mime: "", filename: "turn.webm" };
}

/** Filename for a clip of this mime, for the upload. */
export function filenameFor(mime: string): string {
  return MIME_CHOICES.find((c) => c.mime === mime)?.filename ?? "turn.webm";
}

/** Root-mean-square of a byte-domain waveform, 0..1. */
export function rms(samples: Uint8Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const v = (samples[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / samples.length);
}

export function createRecorder(opts: RecorderOptions): Recorder {
  const Ctor = recorderCtor();
  const silenceMs = opts.silenceMs ?? DEFAULTS.silenceMs;
  const maxSilenceMs = Math.max(silenceMs, opts.maxSilenceMs ?? DEFAULTS.maxSilenceMs);
  const maxMs = opts.maxMs ?? DEFAULTS.maxMs;
  const minSpeechMs = opts.minSpeechMs ?? DEFAULTS.minSpeechMs;

  let stream: MediaStream | null = null;
  let context: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let recorder: MediaRecorder | null = null;
  let chunks: BlobPart[] = [];
  let mime = "";

  /** The caller wants to be listening. Only stop() clears it. */
  let wanted = false;
  let ticker: ReturnType<typeof setInterval> | null = null;
  let tick = 0;

  // Per-turn state.
  let startedAt = 0;
  let lastSpeechAt = 0;
  let speechMs = 0;
  let hadSpeech = false;
  let floor = 0.01; // ambient noise, learned from quiet frames only
  /** Why the recorder was stopped: a turn to deliver, or a discard. */
  let deliver = false;
  /** The longest mid-sentence pause THIS learner has come back from.
   *  Kept for the life of the recorder - the whole conversation - because
   *  a person's rhythm does not change between one sentence and the next.
   *  This is what lets a halting A1 learner earn a five-second window
   *  within their first sentence while a fluent one keeps a snappy reply. */
  let longestResumedPauseMs = 0;

  /** How long to wait after speech before calling the turn finished: the
   *  level's starting window, stretched to cover the pauses this learner
   *  has actually been seen to come back from, capped at the ceiling. */
  const effectiveSilenceMs = () =>
    Math.min(
      maxSilenceMs,
      Math.max(silenceMs, longestResumedPauseMs * RESUMED_PAUSE_FACTOR + RESUMED_PAUSE_SLACK_MS),
    );

  const resetTurn = () => {
    chunks = [];
    startedAt = Date.now();
    lastSpeechAt = 0;
    speechMs = 0;
    hadSpeech = false;
    deliver = false;
  };

  const stopTicker = () => {
    if (ticker !== null) clearInterval(ticker);
    ticker = null;
  };

  const armRecorder = () => {
    if (!Ctor || !stream) return;
    resetTurn();
    const r = mime ? new Ctor(stream, { mimeType: mime }) : new Ctor(stream);
    recorder = r;
    r.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    r.onstop = () => {
      const seconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
      const parts = chunks;
      const shouldDeliver = deliver && hadSpeech && speechMs >= minSpeechMs;
      // Whether or not this one is delivered, the next turn starts
      // listening straight away if the caller still wants it.
      if (wanted) armRecorder();
      if (shouldDeliver) {
        opts.onClip(new Blob(parts, { type: mime || "audio/webm" }), seconds, mime || "audio/webm");
      }
    };
    try {
      r.start(250);
    } catch (e) {
      opts.onError?.(e);
    }
  };

  /** Close the current turn and hand it over. */
  const closeTurn = () => {
    const r = recorder;
    if (!r) return;
    recorder = null;
    deliver = true;
    try {
      r.stop();
    } catch {
      // Already inactive. Its onstop has either fired or never will.
    }
  };

  const sample = () => {
    if (!analyser) return;
    const buf = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buf);
    const level = rms(buf);
    tick += 1;

    const threshold = Math.max(MIN_SPEECH_LEVEL, floor * ABOVE_FLOOR);
    const speaking = level > threshold;
    // Only a frame that is NOT speech teaches us what the room sounds
    // like; a speech frame would teach us that the learner is the noise.
    if (!speaking) floor = Math.min(MAX_FLOOR, floor + (level - floor) * FLOOR_EASE);
    const now = Date.now();
    if (speaking) {
      // Coming back after a real pause teaches us this learner's rhythm.
      if (hadSpeech && lastSpeechAt > 0) {
        const pause = now - lastSpeechAt;
        if (pause >= RESUMED_PAUSE_MIN_MS) {
          longestResumedPauseMs = Math.max(longestResumedPauseMs, pause);
        }
      }
      hadSpeech = true;
      lastSpeechAt = now;
      speechMs += TICK_MS;
    }
    if (tick % LEVEL_EVERY === 0) opts.onLevel?.(Math.min(1, level * 4));

    if (!recorder) return;
    if (hadSpeech && now - lastSpeechAt >= effectiveSilenceMs()) {
      // They stopped a while ago. That is the end of the turn — and if it
      // turns out to have been too short to be one, onstop re-arms anyway.
      closeTurn();
    } else if (now - startedAt >= maxMs) {
      closeTurn();
    }
  };

  return {
    start: async () => {
      if (!Ctor) {
        opts.onError?.(new Error("recording unsupported"));
        return;
      }
      wanted = true;
      try {
        if (!stream) {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          const chosen = pickMime((m) => Ctor.isTypeSupported?.(m) ?? false);
          mime = chosen.mime;
          context = new AudioContext();
          analyser = context.createAnalyser();
          analyser.fftSize = 1024;
          context.createMediaStreamSource(stream).connect(analyser);
        }
        if (!wanted) return; // stopped while the permission prompt was up
        if (!recorder) armRecorder();
        if (ticker === null) ticker = setInterval(sample, TICK_MS);
      } catch (e) {
        wanted = false;
        opts.onError?.(e);
      }
    },
    finish: () => {
      if (recorder && hadSpeech) closeTurn();
    },
    stop: () => {
      wanted = false;
      stopTicker();
      const r = recorder;
      recorder = null;
      deliver = false;
      try {
        r?.stop();
      } catch {
        // Not running.
      }
      stream?.getTracks().forEach((track) => track.stop());
      stream = null;
      void context?.close().catch(() => undefined);
      context = null;
      analyser = null;
      opts.onLevel?.(0);
    },
  };

}
